"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { useRouter } from "@/lib/i18n/navigation";
import { MagnifyingGlass, NavigationArrow, X, Pill, MapPin } from "@phosphor-icons/react";
import { localizedName } from "@/lib/data/i18n-helpers";
import { track } from "@/lib/observability/analytics";

type CityChoice = {
  slug: string;
  nameFr: string;
  nameAr?: string;
  nameEn?: string;
  region: string;
  lat: number;
  lng: number;
  count: number;
};

type Props = {
  cities: CityChoice[];
};

export function PharmacyCitySearch({ cities }: Props) {
  const t = useTranslations("pharmacy");
  const locale = useLocale();
  const router = useRouter();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [nominatimSuggest, setNominatimSuggest] = useState<{
    label: string;
    nearestCity: CityChoice;
    distKm: number;
  } | null>(null);
  const [nomLoading, setNomLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const nomTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const matches = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return cities.slice(0, 8);
    return cities
      .filter(
        (c) =>
          c.nameFr.toLowerCase().includes(query) ||
          (c.nameAr ?? "").toLowerCase().includes(query) ||
          (c.nameEn ?? "").toLowerCase().includes(query) ||
          c.slug.includes(query) ||
          c.region.toLowerCase().includes(query),
      )
      .slice(0, 12);
  }, [q, cities]);

  // Tracking recherche pharmacie de garde — jamais compté jusqu'ici dans
  // l'admin (même gap que MedicamentSearch, cf audit 2026-07-20).
  function go(slug: string) {
    setOpen(false);
    if (q.trim()) {
      track("search:performed", { q: q.trim(), ville: slug, locale, source: "pharmacie-search" });
      try {
        fetch("/api/track/search", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ q: q.trim(), ville: slug, locale, source: "pharmacie-search" }),
          keepalive: true,
          credentials: "same-origin",
        }).catch(() => {});
      } catch {
        // analytics ne doit jamais empêcher la navigation
      }
    }
    router.push(`/pharmacie-de-garde/${slug}` as never);
  }

  function findNearestCity(lat: number, lng: number): { city: CityChoice; distKm: number } | null {
    let best: { city: CityChoice; dist: number } | null = null;
    for (const c of cities) {
      const dlat = (c.lat - lat) * 111;
      const dlng = (c.lng - lng) * 111 * Math.cos((lat * Math.PI) / 180);
      const dist = Math.sqrt(dlat * dlat + dlng * dlng);
      if (!best || dist < best.dist) best = { city: c, dist };
    }
    return best ? { city: best.city, distKm: Math.round(best.dist * 10) / 10 } : null;
  }

  /**
   * Si la query ne matche aucune ville, interroge Nominatim (OSM)
   * pour chercher la localité au Maroc, puis trouve la CITIES Sahha la plus proche.
   */
  async function resolveViaNominatim(query: string) {
    if (!query.trim() || query.trim().length < 3) {
      setNominatimSuggest(null);
      return;
    }
    setNomLoading(true);
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&countrycodes=ma&limit=1&q=${encodeURIComponent(query.trim())}`;
      const res = await fetch(url, { headers: { Accept: "application/json" } });
      if (!res.ok) {
        setNominatimSuggest(null);
        return;
      }
      const data = (await res.json()) as Array<{ lat: string; lon: string; display_name: string }>;
      if (data.length === 0) {
        setNominatimSuggest(null);
        return;
      }
      const lat = parseFloat(data[0]!.lat);
      const lng = parseFloat(data[0]!.lon);
      const nearest = findNearestCity(lat, lng);
      if (nearest) {
        setNominatimSuggest({
          label: data[0]!.display_name.split(",")[0]!.trim(),
          nearestCity: nearest.city,
          distKm: nearest.distKm,
        });
      }
    } catch {
      setNominatimSuggest(null);
    } finally {
      setNomLoading(false);
    }
  }

  function handleGeoloc() {
    setGeoError(null);
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setGeoError(t("search.errors.notSupported"));
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const nearest = findNearestCity(pos.coords.latitude, pos.coords.longitude);
        setGeoLoading(false);
        if (nearest) go(nearest.city.slug);
        else setGeoError(t("search.errors.noNearest"));
      },
      (err) => {
        setGeoLoading(false);
        setGeoError(
          err.code === err.PERMISSION_DENIED
            ? t("search.errors.permissionDenied")
            : t("search.errors.positionFailed"),
        );
      },
      { timeout: 8000, maximumAge: 60_000 },
    );
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(matches.length - 1, a + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const m = matches[active];
      if (m) go(m.slug);
    } else if (e.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
    }
  }

  // Close on outside click — utilise le containerRef qui englobe input + dropdown
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Quand q ne matche aucune ville, propose une suggestion Nominatim (debounce 600ms)
  useEffect(() => {
    if (nomTimerRef.current) clearTimeout(nomTimerRef.current);
    if (!q.trim() || matches.length > 0) {
      setNominatimSuggest(null);
      return;
    }
    nomTimerRef.current = setTimeout(() => {
      resolveViaNominatim(q);
    }, 600);
    return () => {
      if (nomTimerRef.current) clearTimeout(nomTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, matches.length]);

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="flex h-12 items-center gap-2 rounded-full bg-white px-4 shadow-md focus-within:ring-2 focus-within:ring-[var(--sahha-primary-300)]">
        <MagnifyingGlass weight="bold" size={18} className="shrink-0 text-[var(--sahha-primary-700)]" />
        <input
          ref={inputRef}
          type="text"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
            setActive(0);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={t("search.placeholder")}
          className="flex-1 bg-transparent text-[14px] font-medium text-[var(--sahha-text-900)] outline-none placeholder:text-[var(--sahha-text-400)]"
          name="city-search"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          data-form-type="other"
          data-lpignore="true"
          data-1p-ignore="true"
          data-bwignore="true"
        />
        {q && (
          <button
            type="button"
            onClick={() => {
              setQ("");
              inputRef.current?.focus();
            }}
            aria-label={t("search.clear")}
            className="text-[var(--sahha-text-400)]"
          >
            <X weight="bold" size={14} />
          </button>
        )}
        <button
          type="button"
          onClick={handleGeoloc}
          disabled={geoLoading}
          aria-label={t("search.useMyPosition")}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--sahha-primary-50)] text-[var(--sahha-primary-700)] transition active:scale-[0.95] disabled:opacity-60"
        >
          {geoLoading ? (
            <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-[var(--sahha-primary-300)] border-t-[var(--sahha-primary-700)]" />
          ) : (
            <NavigationArrow weight="fill" size={14} />
          )}
        </button>
      </div>

      {geoError && (
        <p className="mt-1.5 px-2 text-[11px] font-semibold text-rose-700">{geoError}</p>
      )}

      {open && (
        <ul className="absolute z-50 mt-2 max-h-[60vh] w-full overflow-y-auto rounded-[18px] bg-white p-1 shadow-[0_20px_40px_-12px_rgba(15,30,58,0.25)] ring-1 ring-[var(--sahha-border-subtle)]">
          {/* Première option : Ma position (toujours visible) */}
          <li>
            <button
              type="button"
              onClick={handleGeoloc}
              disabled={geoLoading}
              className="flex w-full items-center gap-3 rounded-[14px] bg-[var(--sahha-primary-50)] px-3 py-2.5 text-start transition active:scale-[0.99] hover:bg-[var(--sahha-primary-100)] disabled:opacity-60"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--sahha-primary-700)] text-white">
                {geoLoading ? (
                  <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                ) : (
                  <NavigationArrow weight="fill" size={16} />
                )}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[13.5px] font-extrabold text-[var(--sahha-primary-700)]">
                  {t("search.myPosition")}
                </span>
                <span className="block text-[11px] text-[var(--sahha-text-500)]">
                  {t("search.myPositionSubtitle")}
                </span>
              </span>
            </button>
          </li>

          {matches.map((m, i) => (
            <li key={m.slug}>
              <button
                type="button"
                onMouseEnter={() => setActive(i)}
                onClick={() => go(m.slug)}
                className={`flex w-full items-center gap-3 rounded-[14px] px-3 py-2.5 text-start transition ${
                  i === active ? "bg-[var(--sahha-primary-50)]" : ""
                }`}
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--sahha-primary-100)] text-[var(--sahha-primary-700)]">
                  <Pill weight="duotone" size={18} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13.5px] font-bold text-[var(--sahha-text-900)]">
                    {localizedName(m, locale)}
                  </span>
                  <span className="block truncate text-[11px] text-[var(--sahha-text-500)]">
                    {m.region} {m.count > 0 ? t("search.onDutyShort", { count: m.count }) : ""}
                  </span>
                </span>
              </button>
            </li>
          ))}

          {/* Nominatim fallback : si pas de match Sahha mais query résolue via OSM */}
          {matches.length === 0 && nomLoading && (
            <li className="flex items-center justify-center gap-2 px-3 py-3 text-[12px] text-[var(--sahha-text-500)]">
              <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-[var(--sahha-primary-300)] border-t-[var(--sahha-primary-700)]" />
              {t("search.searching")}
            </li>
          )}
          {matches.length === 0 && !nomLoading && nominatimSuggest && (
            <li>
              <button
                type="button"
                onClick={() => go(nominatimSuggest.nearestCity.slug)}
                className="flex w-full items-start gap-3 rounded-[14px] bg-amber-50 px-3 py-3 text-start transition hover:bg-amber-100"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                  <MapPin weight="duotone" size={18} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[11px] font-bold uppercase tracking-wider text-amber-700">
                    {t("search.notCoveredKicker")}
                  </span>
                  <span className="mt-0.5 block truncate text-[13.5px] font-extrabold text-[var(--sahha-text-900)]">
                    {nominatimSuggest.label}
                  </span>
                  <span className="mt-0.5 block text-[11.5px] text-[var(--sahha-text-600)]">
                    {t.rich("search.seeNearestHTML", {
                      city: localizedName(nominatimSuggest.nearestCity, locale),
                      distance: String(nominatimSuggest.distKm),
                      strong: (chunks) => <strong>{chunks}</strong>,
                      span: (chunks) => <span className="text-[var(--sahha-text-400)]">{chunks}</span>,
                    })}
                  </span>
                </span>
              </button>
            </li>
          )}
          {matches.length === 0 && !nomLoading && !nominatimSuggest && q.trim().length >= 3 && (
            <li className="px-3 py-3 text-[12px] text-[var(--sahha-text-500)]">
              {t("search.noCity", { query: q })}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
